import { MultiSelect, Select, createStyles, rem } from "@mantine/core";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import { FiChevronLeft, FiDownload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import styled from "styled-components";
import { Unit } from "../../entities/unit";
import { BiService, MissionaryReportUnit } from "../../services/bi/bi.service";
import { Lesson, LessonService } from "../../services/lesson/lesson.service";
import { UnitService } from "../../services/unit/unit.service";

const useStyles = createStyles((theme) => ({
  root: {
    position: "relative",
    marginBottom: rem(2.4),
  },
  input: {
    height: rem(56),
    paddingTop: rem(18),
  },
  label: {
    position: "absolute",
    pointerEvents: "none",
    fontSize: theme.fontSizes.xs,
    paddingLeft: theme.spacing.sm,
    paddingTop: `calc(${theme.spacing.sm} / 2)`,
    zIndex: 1,
  },
}));

export function RelatorioMissionario() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [units, setUnits] = useState<Unit[]>([]);
  const [rawLessons, setRawLessons] = useState<Lesson[]>([]);
  const [lessons, setLessons] = useState<any[]>([]);
  const [lessonNumbers, setLessonNumbers] = useState<string[]>([]);
  const [unitsSelected, setUnitsSelected] = useState<string[]>([]);
  const [quarterSelected, setQuarterSelected] = useState<string | null>(null);
  const [lessonNumberSelected, setLessonNumberSelected] = useState<string | null>(null);
  const [reportData, setReportData] = useState<MissionaryReportUnit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [reportLabel, setReportLabel] = useState<string>("");

  const fetchInitialData = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const [responseUnits, responseLessons] = await Promise.all([
        UnitService.get(),
        LessonService.getAtDate(),
      ]);

      setUnits(responseUnits);
      setRawLessons(responseLessons);

      let years = responseLessons.map((l) => l.year);
      years = [...new Set(years)];

      const quarterList: any[] = [];
      years.forEach((y) => {
        const quarters = [
          ...new Set(
            responseLessons.filter((l) => l.year === y).map((l) => l.quarter)
          ),
        ];
        quarters.forEach((q) => quarterList.push({ year: y, quarter: q }));
      });

      setLessons(quarterList);
    } catch (error: any) {
      toast.warning(error[0], { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuarterChange = (value: string | null) => {
    setQuarterSelected(value);
    setLessonNumberSelected(null);
    if (!value) {
      setLessonNumbers([]);
      return;
    }
    const yearNum = Number(value.split(";")[0].replace(/[^0-9]/g, ""));
    const quarterNum = Number(value.split(";")[1].replace(/[^0-9]/g, ""));
    const nums = rawLessons
      .filter((l) => l.year === yearNum && l.quarter === quarterNum)
      .map((l) => l.number)
      .sort((a, b) => a - b);
    setLessonNumbers([...new Set(nums)].map(String));
  };

  const fetchReport = async (): Promise<void> => {
    if (!quarterSelected) {
      toast.warning("Selecione um trimestre", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setIsLoading(true);
    setHasSearched(false);
    try {
      const [yearStr, quarterStr] = quarterSelected.split(";");
      const year = Number(yearStr.replace(/[^0-9]/g, ""));
      const quarter = Number(quarterStr.replace(/[^0-9]/g, ""));
      const lesson_number = lessonNumberSelected ? Number(lessonNumberSelected) : undefined;

      const unit_ids: string[] = [];
      unitsSelected.forEach((name) => {
        const unitId = units.find((u) => u.name === name)?.id;
        if (unitId) unit_ids.push(unitId);
      });

      const response = await BiService.getMissionaryActivitiesReport({
        year,
        quarter,
        lesson_number,
        unit_ids: unit_ids.length > 0 ? unit_ids : undefined,
      });

      if (!response?.units?.length) {
        toast.warning("Não há dados para o período selecionado", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setReportData([]);
      } else {
        setReportData(response.units);
        const lessonLabel = lesson_number ? ` — Lição ${lesson_number}` : "";
        setReportLabel(`Ano ${year} — ${quarter}º Trimestre${lessonLabel}`);
      }
    } catch (error: any) {
      toast.warning(error[0] ?? "Erro ao buscar relatório", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsLoading(false);
      setHasSearched(true);
    }
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

    doc.setFontSize(14);
    doc.setTextColor(52, 57, 165);
    doc.text("Relatório de Atividades Missionárias", 14, 18);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(reportLabel, 14, 26);
    doc.text(`Gerado em: ${new Date().toLocaleString("pt-BR")}`, 14, 32);

    const pdfTotals = reportData.reduce(
      (acc, row) => ({
        present: acc.present + row.students_present,
        studied: acc.studied + row.studied_bible_and_lesson,
        group: acc.group + row.participated_small_group,
        bible: acc.bible + row.gave_bible_study,
        baptism: acc.baptism + row.lead_to_baptism,
      }),
      { present: 0, studied: 0, group: 0, bible: 0, baptism: 0 }
    );

    autoTable(doc, {
      startY: 38,
      head: [
        [
          "Classe",
          "Alunos presentes",
          "Estudaram a lição diariamente",
          "Participaram de classe bíblica",
          "Deram estudo bíblico",
          "Levados ao batismo",
          "Lições registradas",
        ],
      ],
      body: [
        ...reportData.map((row) => [
          row.unit_name,
          row.students_present,
          row.studied_bible_and_lesson,
          row.participated_small_group,
          row.gave_bible_study,
          row.lead_to_baptism,
          row.total_lessons,
        ]),
        ["Total", pdfTotals.present, pdfTotals.studied, pdfTotals.group, pdfTotals.bible, pdfTotals.baptism, "—"],
      ],
      headStyles: {
        fillColor: [52, 57, 165],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 9,
      },
      bodyStyles: { fontSize: 9 },
      footStyles: { fontStyle: "bold", fillColor: [240, 240, 240] },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { halign: "center" },
        2: { halign: "center" },
        3: { halign: "center" },
        4: { halign: "center" },
        5: { halign: "center" },
        6: { halign: "center" },
      },
      didParseCell: (data) => {
        if (data.row.index === reportData.length) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [235, 235, 245];
        }
      },
    });

    doc.save(`relatorio-missionario-${reportLabel.replace(/\s|—/g, "-")}.pdf`);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const totals = reportData.reduce(
    (acc, row) => ({
      present: acc.present + row.students_present,
      studied: acc.studied + row.studied_bible_and_lesson,
      group: acc.group + row.participated_small_group,
      bible: acc.bible + row.gave_bible_study,
      baptism: acc.baptism + row.lead_to_baptism,
    }),
    { present: 0, studied: 0, group: 0, bible: 0, baptism: 0 }
  );

  return (
    <OuterWrapper>
      <PageTopBar onClick={() => navigate(-1)}>
        <FiChevronLeft />
        <span>Relatório de Atividades Missionárias</span>
      </PageTopBar>

    <PageWrapper>

      <FiltersCard>
        <MultiSelect
          mt="md"
          withinPortal
          data={units.map((u) => u.name) as any}
          placeholder="Todas as classes"
          label="Classe(s)"
          classNames={classes}
          onChange={setUnitsSelected}
          disabled={isLoading}
        />

        <Select
          mt="md"
          withinPortal
          data={lessons.map(
            (l: any) => `Ano: ${l.year}; Trimestre: ${l.quarter}`
          )}
          placeholder="Selecione um trimestre"
          label="Trimestre"
          classNames={classes}
          onChange={handleQuarterChange}
          disabled={isLoading}
        />

        <Select
          mt="md"
          withinPortal
          data={lessonNumbers.map((n) => ({ value: n, label: `Lição ${n}` }))}
          placeholder="Todas as lições"
          label="Lição (opcional)"
          classNames={classes}
          value={lessonNumberSelected}
          onChange={setLessonNumberSelected}
          disabled={isLoading || !quarterSelected || lessonNumbers.length === 0}
          clearable
        />

        <ActionsRow>
          <SearchButton onClick={fetchReport} disabled={isLoading}>
            {isLoading ? (
              <Spinner color="light" style={{ width: 18, height: 18 }} />
            ) : (
              <label style={{ cursor: "pointer" }}>Buscar</label>
            )}
          </SearchButton>

          {hasSearched && reportData.length > 0 && (
            <ExportButton onClick={exportPDF}>
              <FiDownload size={16} />
              <label style={{ cursor: "pointer" }}>Exportar PDF</label>
            </ExportButton>
          )}
        </ActionsRow>
      </FiltersCard>

      {hasSearched && reportData.length > 0 && (
        <>
          <ReportLabel>{reportLabel}</ReportLabel>

          {/* Tabela — visível em telas maiores */}
          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <Th>Classe</Th>
                  <Th center>Alunos presentes</Th>
                  <Th center>Estudaram a lição diariamente</Th>
                  <Th center>Participaram de classe bíblica</Th>
                  <Th center>Deram estudo bíblico</Th>
                  <Th center>Levados ao batismo</Th>
                  <Th center>Lições registradas</Th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr key={index}>
                    <Td>{row.unit_name}</Td>
                    <Td center>{row.students_present}</Td>
                    <Td center>{row.studied_bible_and_lesson}</Td>
                    <Td center>{row.participated_small_group}</Td>
                    <Td center>{row.gave_bible_study}</Td>
                    <Td center>{row.lead_to_baptism}</Td>
                    <Td center muted>{row.total_lessons}</Td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <TfTd>Total</TfTd>
                  <TfTd center>{totals.present}</TfTd>
                  <TfTd center>{totals.studied}</TfTd>
                  <TfTd center>{totals.group}</TfTd>
                  <TfTd center>{totals.bible}</TfTd>
                  <TfTd center>{totals.baptism}</TfTd>
                  <TfTd center>—</TfTd>
                </tr>
              </tfoot>
            </StyledTable>
          </TableWrapper>

          {/* Rodapé de totais — visível em todas as telas */}
          <SummaryFooter>
            <SummaryFooterTitle>Totais gerais</SummaryFooterTitle>
            <SummaryGrid>
              <SummaryItem>
                <SummaryItemValue>{totals.present}</SummaryItemValue>
                <SummaryItemLabel>Alunos presentes</SummaryItemLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryItemValue>{totals.studied}</SummaryItemValue>
                <SummaryItemLabel>Estudaram a lição diariamente</SummaryItemLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryItemValue>{totals.group}</SummaryItemValue>
                <SummaryItemLabel>Participaram de classe bíblica</SummaryItemLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryItemValue>{totals.bible}</SummaryItemValue>
                <SummaryItemLabel>Deram estudo bíblico</SummaryItemLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryItemValue>{totals.baptism}</SummaryItemValue>
                <SummaryItemLabel>Levados ao batismo</SummaryItemLabel>
              </SummaryItem>
            </SummaryGrid>
          </SummaryFooter>

          {/* Cards — visíveis apenas em mobile */}
          <CardsWrapper>
            {reportData.map((row, index) => (
              <UnitCard key={index}>
                <UnitCardTitle>{row.unit_name}</UnitCardTitle>
                <MetricRow>
                  <MetricLabel>Alunos presentes</MetricLabel>
                  <MetricValue>{row.students_present}</MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>Estudaram a lição diariamente</MetricLabel>
                  <MetricValue>{row.studied_bible_and_lesson}</MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>Participaram de classe bíblica</MetricLabel>
                  <MetricValue>{row.participated_small_group}</MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>Deram estudo bíblico</MetricLabel>
                  <MetricValue>{row.gave_bible_study}</MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>Levados ao batismo</MetricLabel>
                  <MetricValue>{row.lead_to_baptism}</MetricValue>
                </MetricRow>
                <MetricRow muted>
                  <MetricLabel>Lições registradas</MetricLabel>
                  <MetricValue muted>{row.total_lessons}</MetricValue>
                </MetricRow>
              </UnitCard>
            ))}

            <TotalsCard>
              <UnitCardTitle>Total geral</UnitCardTitle>
              <MetricRow>
                <MetricLabel>Alunos presentes</MetricLabel>
                <MetricValue>{totals.present}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>Estudaram a lição diariamente</MetricLabel>
                <MetricValue>{totals.studied}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>Participaram de classe bíblica</MetricLabel>
                <MetricValue>{totals.group}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>Deram estudo bíblico</MetricLabel>
                <MetricValue>{totals.bible}</MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>Levados ao batismo</MetricLabel>
                <MetricValue>{totals.baptism}</MetricValue>
              </MetricRow>
            </TotalsCard>
          </CardsWrapper>
        </>
      )}

      {hasSearched && reportData.length === 0 && (
        <EmptyState>Nenhum dado encontrado para o período selecionado.</EmptyState>
      )}
    </PageWrapper>
    </OuterWrapper>
  );
}

/* ─── Layout ─── */

const OuterWrapper = styled.div`
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background-color: #f0f0f5;
`;

const PageTopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1.6rem 2rem;
  background-color: #3439a5;
  color: #fff;
  font-size: 1.8rem;
  font-weight: 500;
  cursor: pointer;

  svg {
    flex-shrink: 0;
    font-size: 2.2rem;
  }

  @media (min-width: 768px) {
    padding: 1.8rem 3.2rem;
    font-size: 2rem;
  }
`;

const PageWrapper = styled.div`
  padding: 1.6rem 1.2rem;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;

  @media (min-width: 768px) {
    padding: 2.4rem 3.2rem;
    gap: 2rem;
  }
`;

const FiltersCard = styled.div`
  background: #fff;
  border: 1px solid #c0c0c9;
  border-radius: 0.8rem;
  padding: 1.6rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  @media (min-width: 768px) {
    padding: 1.6rem 2rem;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-top: 0.8rem;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const SearchButton = styled.button`
  flex: 1;
  min-width: 120px;
  height: 3.5rem;
  border-radius: 0.4rem;
  background-color: #3439a5;
  border: 0.1rem solid #2b2fa3;
  display: flex;
  align-items: center;
  justify-content: center;

  label {
    color: #fff;
    font-size: 1.4rem;
    font-weight: bold;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const ExportButton = styled.button`
  flex: 1;
  min-width: 120px;
  height: 3.5rem;
  border-radius: 0.4rem;
  background-color: #198754;
  border: 0.1rem solid #157347;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: #fff;

  label {
    color: #fff;
    font-size: 1.4rem;
    font-weight: bold;
  }
`;

const ReportLabel = styled.p`
  font-size: 1.3rem;
  color: #7a7a80;
  margin: 0;
  text-align: center;
`;

/* ─── Tabela (tablet/desktop) ─── */

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #c0c0c9;
  border-radius: 0.8rem;
  background: #fff;

  @media (max-width: 767px) {
    display: none;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.3rem;
`;

const Th = styled.th<{ center?: boolean }>`
  background: #3439a5;
  color: #fff;
  padding: 1rem 1.2rem;
  text-align: ${({ center }) => (center ? "center" : "left")};
  font-weight: 600;
  white-space: nowrap;
  font-size: 1.2rem;
`;

const Td = styled.td<{ center?: boolean; muted?: boolean }>`
  padding: 0.9rem 1.2rem;
  border-bottom: 1px solid #eee;
  text-align: ${({ center }) => (center ? "center" : "left")};
  color: ${({ muted }) => (muted ? "#aaa" : "#47474d")};
  font-weight: ${({ center }) => (center ? "500" : "400")};

  tr:hover & {
    background: #f5f5fb;
  }
`;

const TfTd = styled.td<{ center?: boolean }>`
  padding: 1rem 1.2rem;
  text-align: ${({ center }) => (center ? "center" : "left")};
  font-weight: 700;
  background: #eeeef8;
  color: #3439a5;
  font-size: 1.3rem;
`;

/* ─── Cards (mobile) ─── */

const CardsWrapper = styled.div`
  display: none;
  flex-direction: column;
  gap: 1.2rem;

  @media (max-width: 767px) {
    display: flex;
  }
`;

const UnitCard = styled.div`
  background: #fff;
  border: 1px solid #c0c0c9;
  border-radius: 0.8rem;
  padding: 1.4rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const TotalsCard = styled(UnitCard)`
  border-color: #3439a5;
  background: #f0f0fb;
`;

const UnitCardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: #3439a5;
  margin: 0 0 0.4rem 0;
  border-bottom: 1px solid #e0e0ee;
  padding-bottom: 0.6rem;
`;

const MetricRow = styled.div<{ muted?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.8rem;
  opacity: ${({ muted }) => (muted ? 0.6 : 1)};
`;

const MetricLabel = styled.span`
  font-size: 1.3rem;
  color: #7a7a80;
  flex: 1;
`;

const MetricValue = styled.span<{ muted?: boolean }>`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ muted }) => (muted ? "#aaa" : "#3439a5")};
  min-width: 32px;
  text-align: right;
`;

/* ─── Rodapé de totais ─── */

const SummaryFooter = styled.div`
  background: #3439a5;
  border-radius: 0.8rem;
  padding: 1.6rem 1.4rem;
`;

const SummaryFooterTitle = styled.h4`
  color: rgba(255, 255, 255, 0.75);
  font-size: 1.1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 1.2rem 0;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 0.6rem;
  padding: 1rem 0.8rem;
  text-align: center;
`;

const SummaryItemValue = styled.span`
  font-size: 2.2rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const SummaryItemLabel = styled.span`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.3;
`;

/* ─── Estado vazio ─── */

const EmptyState = styled.p`
  text-align: center;
  color: #888;
  font-size: 1.4rem;
  padding: 2rem 0;
`;
